import {
    Component,
    OnInit,
    Input
} from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { DomSanitizer } from '@angular/platform-browser';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { Breadcrumb, BreadcrumbGroup } from './breadcrumb.interface';

const LINK_KEY = '{link}';
const loggerBaseMessage = `FINDER-BREADCRUMB --`;

/**
 * Takes a string and links object and generates a breadcrumb
 *
 * @example <finder-breadcrumb [links]="breadcrumbLinks" breadcrumb="{{breadcrumbString}}"></finder-breadcrumb>
 * @component BreadcrumbComponent
 * @argument breadcrumb string
 * @returns iterable object[]
 */
@Component({
    selector: 'finder-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    'preserveWhitespaces': true
})
export class BreadcrumbComponent implements OnInit {

    // Object with links info
    // [{label: 'Attraction', link: 'http://disney.com/attractions', title: 'Go to Attractions'}]
    @Input() public links: Breadcrumb[];

    // Breadcrumb string to process
    // {link} located in {link} at {link}
    @Input() public breadcrumb: string;

    // Boolean to decide if the complete text content should be read first
    @Input() public hasFullContentReader: boolean;

    public breadcrumbIterable: Array<Breadcrumb | BreadcrumbGroup> = [];

    constructor(
        private logger: LoggerService,
        private sanitizer: DomSanitizer,
        private debugLogger: DebugLogService
    ) { }

    ngOnInit() {
        if (!isEmpty(this.breadcrumb)) {
            this.breadcrumbIterable = this.breadcrumbBuilder(this.breadcrumb, this.links);
        } else {
            this.logger.error(`${loggerBaseMessage} There's no breadcrumb defined`);
        }
    }

    trustUrl(url) {
        // A normal relative URL
        if (url.indexOf('/') === 0) {
            return url;
        }

        // on hybrid these links can be deep link to go to a native app
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }

    private breadcrumbBuilder(breadcrumb: string, links: Breadcrumb[]): Array<Breadcrumb | BreadcrumbGroup> {
        const breadcrumbItems: Array<Breadcrumb | BreadcrumbGroup> = [];
        const newBreak = '^';
        let linksIndex = 0;

        /**
         * Our breadcrumbs can look like this
         * "{link} located in {link} at {link}"
         *
         * Or for some languages like this
         * {link}位於 {link}香港迪士尼樂園{link} 探險世界{link} {link} 探險世界
         *
         * We want to preserve the lack of spacing when they appear
         */

        /**
         * Add a link to the array. Able to remove spacing for words
         */
        const getLinkForBreadcrumb = (): Breadcrumb => {
            // Had to use index lookup. "shift" was breaking the tests
            const link: Breadcrumb = links[linksIndex++];

            // Check to make sure we have a link
            if(!link) {
                this.logger.warn(
                    `${loggerBaseMessage} There's an empty link in the array`
                );

                // Return an empty object so things don't break in the template
                return {};
            }

            return link;
        };

        const getTextForBreadcrumb = (label): Breadcrumb => {
            let newItem: Breadcrumb = {};

            if (label.length > 0) {
                newItem = { label };
            }

            return newItem;
        };

        // Keep track of the type of breadcrumb
        this.debugLogger.log(`breadcrumb: string`, breadcrumb);

        /**
         * First we'll split on the space. That way we can parse this one bit at a time
         */
        breadcrumb.trim().split(' ').forEach(part => {
            /**
             * Test for a simple string. If it doesn't match our link placeholder then
             * move on.
             */
            if (!part.includes(LINK_KEY)) {
                breadcrumbItems.push(getTextForBreadcrumb(part));

                return;
            }

            /**
             * If the part is equal to the placeholder add a link to
             * the array and move on.
             */
            if (part === LINK_KEY) {
                breadcrumbItems.push(getLinkForBreadcrumb());

                return;
            }

            /**
             * Now things are a bit more complex with a string like
             * {link}香港迪士尼樂園{link}
             * {link}香港迪{link}尼樂園{link}
             * 香港迪士尼樂園{link}
             * {link}香港迪士尼樂園
             */

            // Replace the {link} with ^ so we can look for the spot to add a link
            const complexString = part.replaceAll(LINK_KEY, newBreak);
            let word = ''; // This is built in the for loop
            const complexItem: BreadcrumbGroup = {
                group: true,
                items: []
            };

            // Walk through the new string one char at a time
            for (const char of complexString) {
                // Add a link to the array
                if (char === newBreak) {
                    // If a word exists add it to the array
                    if (word) {
                        complexItem.items.push(getTextForBreadcrumb(word));

                        // Clear the word
                        word = '';
                    }

                    // Add the link to the array
                    complexItem.items.push(getLinkForBreadcrumb());
                    continue;
                }

                // Build the word
                word += char;
            }

            // If we still have some characters in the word they should be added to the array
            if (word) {
                complexItem.items.push(getTextForBreadcrumb(word));
            }

            // Add the complex item
            breadcrumbItems.push(complexItem);
        });

        return breadcrumbItems;
    }
}
